/**
 * Type definitions and constants for My Referral Network
 *
 * This file contains all types, interfaces, and related constants for the referral network feature.
 * Organized by domain (Settings, Members, Validation, etc.)
 */

// ============================================================================
// REFERRAL NETWORK TYPES
// ============================================================================

/**
 * Referral network type configuration
 * Groups related data: IDs, names, and descriptions
 */
export const REFERRAL_NETWORK_TYPES = {
  WATERFALL: {
    ID: '472a5820-a3bc-11f0-aac0-fffe8d0ace8d',
    NAME: 'waterfall_referrals',
    DESCRIPTION:
      'Sequential attorney case system where cases are offered one attorney at a time',
  },
  BROADCAST: {
    ID: '472a645a-a3bc-11f0-aac0-33cd1caadcf8',
    NAME: 'broadcast_referrals',
    DESCRIPTION:
      'Simultaneous case system where cases are sent to multiple attorneys at once',
  },
} as const

// ============================================================================
// BROADCAST SETTINGS TYPES
// ============================================================================

/**
 * Reference tick labels shown on the Time per Round slider (hours). These are
 * display markers only — the slider allows ANY whole-hour value in range
 * (ENG-1724), consistent with the loosened backend validation (ENG-1723).
 */
export const BROADCAST_TIME_PER_ROUND_OPTIONS = [1, 24, 48, 72] as const

/**
 * Time per round is any whole number of hours within
 * [BROADCAST_TIME_PER_ROUND_MIN, BROADCAST_TIME_PER_ROUND_MAX].
 */
export type BroadcastTimePerRound = number
export const BROADCAST_TIME_PER_ROUND_MIN = 1
export const BROADCAST_TIME_PER_ROUND_MAX = 72
export const BROADCAST_TIME_PER_ROUND_STEP = 1

/**
 * Valid options for marketplace fallback rounds in broadcast mode
 */
export const BROADCAST_FALLBACK_ROUNDS_OPTIONS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
] as const
export type BroadcastFallbackRounds =
  (typeof BROADCAST_FALLBACK_ROUNDS_OPTIONS)[number]

/**
 * Broadcast-specific settings (only applicable when type is broadcast_referrals)
 */
export interface BroadcastSettings {
  /** Number of attorneys to include in each broadcast round (1-10, default: 5) */
  attorneys_per_round: number
  /** Time in hours for each round: any whole hour 1-72 (default: 24) */
  time_per_round: BroadcastTimePerRound
  /** Number of rounds before marketplace fallback: 1-10 (default: 4) */
  marketplace_fallback_rounds: BroadcastFallbackRounds
}

/**
 * Default broadcast settings values
 */
export const DEFAULT_BROADCAST_SETTINGS: BroadcastSettings = {
  attorneys_per_round: 5,
  time_per_round: 24,
  marketplace_fallback_rounds: 4,
}

/**
 * Type name union for type safety
 */
export type ReferralNetworkTypeName =
  (typeof REFERRAL_NETWORK_TYPES)[keyof typeof REFERRAL_NETWORK_TYPES]['NAME']

// ============================================================================
// VALIDATION & DEFAULTS
// ============================================================================

/**
 * Default fee percentage for network members
 */
export const DEFAULT_FEE_PERCENTAGE = 25

/**
 * Validation constraints for settings
 */
export const VALIDATION_CONSTRAINTS = {
  TIME_PER_ATTORNEY: {
    MIN: 1,
    MAX: 96,
    ERROR_MESSAGE: 'Time must be between 1 and 96 hours',
  },
  MAXIMUM_ATTORNEYS: {
    MIN: 1,
    MAX: 10,
  },
  BROADCAST: {
    ATTORNEYS_PER_ROUND: {
      MIN: 1,
      MAX: 10,
      DEFAULT: 5,
      ERROR_MESSAGE: 'Attorneys per round must be between 1 and 10',
    },
    TIME_PER_ROUND: {
      MIN: BROADCAST_TIME_PER_ROUND_MIN,
      MAX: BROADCAST_TIME_PER_ROUND_MAX,
      DEFAULT: 24 as BroadcastTimePerRound,
      ERROR_MESSAGE: 'Time per round must be between 1 and 72 hours',
    },
    MARKETPLACE_FALLBACK_ROUNDS: {
      OPTIONS: BROADCAST_FALLBACK_ROUNDS_OPTIONS,
      DEFAULT: 4 as BroadcastFallbackRounds,
      ERROR_MESSAGE: 'Marketplace fallback must be between 1 and 10 rounds',
    },
  },
} as const

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * API endpoint constants
 */
export const API_ENDPOINTS = {
  REFERRAL_NETWORK: '/v1/my-referral-network/referral-network',
  NETWORK_MEMBERS: '/v1/my-referral-network/members',
  NETWORK_MEMBER_BY_ID: (attorneyId: string) =>
    `/v1/my-referral-network/members/${attorneyId}`,
  NETWORK_MEMBER_JURISDICTIONS: (attorneyId: string) =>
    `/v1/my-referral-network/members/${attorneyId}/jurisdictions`,
  WATERFALL_MEMBERS: '/v1/my-referral-network/waterfall-members',
  INVITED_MEMBERS: '/v1/my-referral-network/invited-members',
  DISMISS_INVITED_MEMBER: (attorneyId: string) =>
    `/v1/my-referral-network/invited-members/${attorneyId}/dismiss`,
  RECORD_VISIT: '/v1/my-referral-network/invited-members/record-visit',
} as const

export const DISMISSED_SUGGESTION_KEY = 'hasRemovedSuggestion'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents a network member from Algolia search results
 */
export interface NetworkMember {
  id: string
  profileImage: string
  name: string
  firmName: string
  practiceAreas: string[]
  jurisdictions: string[]
  feePercentage: number
}

/**
 * Attorney details from backend API
 */
export interface AttorneyDetails {
  first_name: string
  last_name: string
  email: string
  is_vetted: boolean
}

/**
 * Network member response from backend API
 */
/**
 * A jurisdiction option (catalog_us_state) the attorney is licensed in.
 */
export interface LicensedJurisdiction {
  id: string
  name: string
}

export interface ReferralNetworkMemberResponse {
  id: string
  network_id: string
  attorney_id: string
  attorney: AttorneyDetails
  fee_percentage: number
  is_active: boolean
  added_ts: string
  last_assignation_ts: string | null
  /**
   * Selected jurisdictions (catalog_us_state UUIDs). An empty array means the
   * member is unrestricted — routed for ALL of their licensed jurisdictions.
   * (ENG-1564)
   */
  allowed_jurisdiction_ids?: string[]
  /**
   * The full option set: every jurisdiction the attorney is licensed in.
   * Present on GET /members/:id and PATCH .../jurisdictions responses.
   */
  licensed_jurisdictions?: LicensedJurisdiction[]
}

/**
 * API response structure for network member
 */
export interface ReferralNetworkMemberApiResponse {
  data: ReferralNetworkMemberResponse
}

/**
 * Referral network type definition
 */
export interface ReferralNetworkType {
  id: string
  name: string // e.g. "waterfall_referrals"
  description: string
}

/**
 * Settings for how the referral network operates (matches backend API)
 */
export interface ReferralNetworkSettings {
  id?: string
  organization_id?: string
  type?: ReferralNetworkType
  /** Time in hours to wait for attorney response before moving to next (Waterfall mode) */
  time_per_attorney: number
  /** Maximum number of attorneys before posting to public marketplace (Waterfall mode) */
  maximum_attorneys: number
  /** Broadcast-specific settings (only present when type is broadcast_referrals) */
  broadcast_settings?: BroadcastSettings
  is_active?: boolean
  created_ts?: string
  updated_ts?: string
}

/**
 * API response structure for network settings
 */
export interface ReferralNetworkSettingsApiResponse {
  data: ReferralNetworkSettings
}

/**
 * Context interface for the Referral Network page
 */
export interface ReferralNetworkContext {
  // Data
  /** Current network settings (null until loaded from backend) */
  settings: ReferralNetworkSettings | null
  /** Initial waterfall members shown on first visit */
  waterfallMembers: NetworkMember[]
  /** Attorneys who visited the org's invite link */
  invitedMembers: NetworkMember[]

  // Loading and Error States
  /** Whether settings are currently being fetched */
  isLoadingSettings: boolean
  /** Whether settings are currently being updated */
  isUpdatingSettings: boolean
  /** Error message if settings fetch/update failed */
  settingsError: string | null
  /** Whether invited members are currently being fetched */
  isLoadingInvitedMembers: boolean

  // Actions
  /** Fetch suggested waterfall members for new users */
  getWaterfallMembers: () => Promise<void>
  /**
   * Add multiple members to the network with specified fee percentages and,
   * optionally, the jurisdictions each member is restricted to (omit = all
   * licensed). Jurisdiction ids are catalog_us_state UUIDs.
   */
  addMembersToNetwork: (
    attorneyIds: string[],
    feePercentages: Record<string, number>,
    jurisdictionIds?: Record<string, string[]>
  ) => Promise<void>
  /** Get a specific network member by attorney ID */
  getNetworkMember: (
    attorneyId: string
  ) => Promise<ReferralNetworkMemberResponse>
  /** Update an existing network member's fee percentage */
  updateNetworkMember: (
    attorneyId: string,
    feePercentage: number
  ) => Promise<void>
  /**
   * Replace a member's allowed-jurisdiction set (full replace; `[]` clears the
   * restriction → member becomes unrestricted). Jurisdiction ids are
   * catalog_us_state UUIDs.
   */
  updateNetworkMemberJurisdictions: (
    attorneyId: string,
    jurisdictionIds: string[]
  ) => Promise<void>
  /** Remove a member from the network */
  removeNetworkMember: (attorneyId: string) => Promise<void>
  /** Retrieve current network settings */
  getNetworkSettings: () => Promise<ReferralNetworkSettings>
  /** Update network settings */
  updateNetworkSettings: (
    settings: Partial<ReferralNetworkSettings>
  ) => Promise<void>
  /** Fetch attorneys who visited the org's invite link */
  getInvitedMembers: () => Promise<void>
  /** Dismiss an invited member suggestion (optimistic with rollback) */
  dismissInvitedMember: (attorneyId: string) => Promise<void>
  /** Remove invited members from local state after they've been added to the network */
  removeInvitedMembersFromList: (memberIds: string[]) => void
}
